"use client";

import {PublicOfferSummary} from "./PublicOfferSummary";
import {AcceptedPassesSection} from "../shared/AcceptedPassesSection";
import {BookingRulesSection} from '../shared/BookingRulesSection';
import {PublicOfferDetailsTrigger} from "./PublicOfferDetailsTrigger";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { formatLocalDateRangeWithOptions } from "@fitro/date-time";
import { useTranslation } from "../../../hooks/useTranslation";
import { formatPriceMinor } from "../../../lib/formatPriceMinor";
import { getPublicCoursePath } from "../../../lib/public-course-links";
import type { CoursePricing } from "../../../services/public-courses.service";
import type { PublicContractOffer } from "../../../services/public-contracts.service";
import type {
  PublicPassOffer,
  PublicPricingClass,
  PublicPrivateBookingOffer,
} from "../../../services/public-passes.service";
import { Modal } from "../../ui";
import {
  PublicPricingTable,
  PricingConditions,
  pricingCell,
  pricingName,
  pricingAmount,
  pricingBody,
} from "./PublicPricingTable";
import { PublicPassOfferCard } from "./PublicPassOfferCard";
import { PublicEventPricing } from "./PublicEventPricing";
import type { PublicScheduleEvent } from "../../../services/public-schedule.service";
import { PublicClassPricing } from "./PublicClassPricing";
import {
  PublicSiteShell,
  PUBLIC_SITE_CONTENT_CLASS_NAME,
} from "./PublicSiteShell";

import { uniquePricingClasses } from "./publicPricingModel";

type Props = {
  classes?: PublicPricingClass[];
  events?: PublicScheduleEvent[];
  eventsError?: boolean;
  offers: PublicPassOffer[];
  accessTemplates?: import("../../../services/public-passes.service").PublicAccessPass[];
  partnerName: string;
  contractOffers?: PublicContractOffer[];
  privateBookingOffers?: PublicPrivateBookingOffer[];
  courses?: CoursePricing[];
  coursesError?: boolean;
  coursesPage?: { number: number; totalPages: number };
  showAboutPage?: boolean;
};

export function PublicPricingPage({
  classes = [],
  events = [],
  eventsError = false,
  offers,
  accessTemplates = [],
  partnerName,
  contractOffers = [],
  privateBookingOffers = [],
  courses = [],
  coursesError = false,
  coursesPage,
  showAboutPage = true,
}: Props) {
  const { locale, t } = useTranslation("landing");
  const m = useTranslations("landing.publicEntry.pricing.compact");
  const courseCopy = useTranslations("client.courses");
  const common = useTranslations("common");
  const money = (n: number, c = "PLN") => formatPriceMinor(n, c, locale);
  // Benefit passes have a section of their own on the public price list; the card badge names the card.
  const groups = [
    {
      key: "entryPasses",
      items: offers.filter(
        (p) =>
          !p.isBenefit &&
          p.type !== "one_time" &&
          p.entriesTotal !== 1 &&
          p.type !== "open" &&
          typeof p.entriesTotal === "number",
      ),
    },
    {
      key: "openPasses",
      items: offers.filter(
        (p) =>
          !p.isBenefit &&
          p.type !== "one_time" &&
          p.entriesTotal !== 1 &&
          (p.type === "open" || typeof p.entriesTotal !== "number"),
      ),
    },
    {
      key: "singleEntries",
      items: offers.filter(
        (p) => !p.isBenefit && (p.type === "one_time" || p.entriesTotal === 1),
      ),
    },
    { key: "benefits", items: offers.filter((p) => p.isBenefit) },
  ];
  const currentEvents = events.filter(
    (event) =>
      !event.isCyclical &&
      event.status !== "cancelled" &&
      event.availabilityStatus !== "occurred",
  );
  const hasAny =
    currentEvents.length ||
    eventsError ||
    classes.length ||
    offers.length ||
    contractOffers.length ||
    privateBookingOffers.length ||
    courses.length ||
    coursesError;
  const renderPassGroup = (g: (typeof groups)[number]) => (
    <PriceSection
      key={g.key}
      id={g.key}
      count={g.items.length}
      title={t(`publicEntry.pricing.sections.${g.key}`)}
    >
      <ul className="grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3">
        {g.items.map((offer) => (
          <li key={offer.id}>
            <PublicPassOfferCard offer={offer} />
          </li>
        ))}
      </ul>
    </PriceSection>
  );
  return (
    <PublicSiteShell
      activePage="pricing"
      partnerName={partnerName}
      showAboutPage={showAboutPage}
    >
      <div
        data-public-entry-pricing-content
        className={`${PUBLIC_SITE_CONTENT_CLASS_NAME} px-5 pb-14 pt-5 sm:px-8 lg:px-10`}
      >
        <h1 className="sr-only">{t("publicEntry.pricing.title")}</h1>
        {!hasAny ? (
          <p className="text-sm text-slate-500">
            {t("publicEntry.pricing.empty")}
          </p>
        ) : null}
        <div className="space-y-10">
          {groups.filter((g) => g.items.length).map(renderPassGroup)}
          {classes.length > 0 ? (
            <PriceSection
              id="wejscia"
              title={m("entriesHeading")}
              count={uniquePricingClasses(classes).length}
            >
              <PublicClassPricing classes={classes} passes={accessTemplates} />
            </PriceSection>
          ) : null}
          {eventsError ? (
            <p role="status">
              <a href="/cennik">{t("publicEntry.pricing.eventsLoadError")}</a>
            </p>
          ) : currentEvents.length > 0 ? (
            <PriceSection
              id="wydarzenia"
              title={t("publicEntry.events.title")}
              count={currentEvents.length}
            >
              <PublicEventPricing events={currentEvents} />
            </PriceSection>
          ) : null}
          {privateBookingOffers.length > 0 ? (
            <PriceSection
              id="indywidualne"
              count={privateBookingOffers.length}
              title={m("privateHeading")}
            >
              <PublicPricingTable
                label={m("privateHeading")}
                headers={[
                  m("priceColumn"),
                  m("serviceColumn"),
                  m("durationColumn"),
                ]}
              >
                {privateBookingOffers.map((o, i) => (
                  <tbody key={`${o.name}-${i}`} className={pricingBody}>
                    <tr>
                      <td
                        className={`${pricingCell} text-left text-lg font-semibold leading-7 tabular-nums text-slate-900 sm:text-[28px]`}
                      >
                        {money(o.priceMinor, o.currency)}
                      </td>
                      <th
                        scope="row"
                        className={`${pricingCell} text-base font-semibold leading-6 text-slate-900 sm:text-xl sm:leading-7`}
                      >
                        <PublicOfferDetailsTrigger name={o.name} description={o.description} className="text-left hover:underline underline-offset-4" summary={
                          <PublicOfferSummary instructors={o.instructors} locations={o.locations} durationMinutes={o.durationMinutes}
                            price={<span className="tabular-nums">{money(o.priceMinor, o.currency)}</span>}/>
                        }>
                          {o.allowedPassTemplates?.length ? <AcceptedPassesSection passes={o.allowedPassTemplates}/> : null}
                          <BookingRulesSection rules={o.bookingRules} inline/>
                        </PublicOfferDetailsTrigger>
                      </th>
                      <td className={`${pricingCell} text-right`}>
                        {o.durationMinutes} min
                      </td>
                    </tr>
                  </tbody>
                ))}
              </PublicPricingTable>
            </PriceSection>
          ) : null}
          {contractOffers.length > 0 ? (
            <PriceSection
              id="umowy"
              count={contractOffers.length}
              title={t("publicEntry.pricing.sections.contracts")}
            >
              <PublicPricingTable
                label={t("publicEntry.pricing.sections.contracts")}
                headers={[
                  m("paymentsColumn"),
                  m("offerColumn"),
                  m("durationColumn"),
                ]}
              >
                {contractOffers.map((o) => (
                  <ContractRow key={o.id} offer={o} />
                ))}
              </PublicPricingTable>
            </PriceSection>
          ) : null}
          {coursesError ? (
            <a href="/cennik">{courseCopy("loadError")}</a>
          ) : courses.length > 0 ? (
            <PriceSection
              id="kursy"
              title={t("publicEntry.nav.courses")}
              count={courses.length}
            >
              <PublicPricingTable
                label={t("publicEntry.nav.courses")}
                headers={[m("priceColumn"), m("offerColumn"), m("datesColumn")]}
              >
                {courses.map((c) => (
                  <tbody key={c.id} className={pricingBody}>
                    <tr>
                      <td
                        className={`${pricingCell} ${pricingAmount} text-left`}
                      >
                        {money(c.totalAmountMinor)}
                      </td>
                      <th
                        scope="row"
                        className={`${pricingCell} ${pricingName}`}
                      >
                        <Link
                          href={getPublicCoursePath(c)}
                          className="underline-offset-4 hover:underline"
                        >
                          {c.name}
                        </Link>
                      </th>
                      <td className={`${pricingCell} text-right`}>
                        {formatLocalDateRangeWithOptions(
                          c.startDate,
                          c.endDate,
                          locale,
                          { day: "numeric", month: "2-digit", year: "numeric" },
                          undefined,
                          { separator: "hyphen" },
                        )}
                      </td>
                    </tr>
                  </tbody>
                ))}
              </PublicPricingTable>
            </PriceSection>
          ) : null}
          {coursesPage && coursesPage.totalPages > 1 ? (
            <nav className="flex justify-end gap-4 text-sm">
              {coursesPage.number > 0 ? (
                <a href={`/cennik?coursePage=${coursesPage.number - 1}`}>
                  {common("actions.back")}
                </a>
              ) : null}
              {coursesPage.number + 1 < coursesPage.totalPages ? (
                <a href={`/cennik?coursePage=${coursesPage.number + 1}`}>
                  {common("actions.next")}
                </a>
              ) : null}
            </nav>
          ) : null}
        </div>
      </div>
    </PublicSiteShell>
  );
}
function PriceSection({
  count,
  id,
  title,
  children,
}: {
  count: number;
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-24">
      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2
          id={`${id}-title`}
          className="text-[23px] font-semibold tracking-tight text-[var(--landing-neutral-900)]"
        >
          {title}
        </h2>
        <span className="inline-flex min-w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 px-2 py-1 text-sm font-medium tabular-nums text-slate-500">
          {count}
        </span>
        <span
          aria-hidden="true"
          className="h-px min-w-8 flex-1 bg-slate-200/80"
        />
      </div>
      {children}
    </section>
  );
}
function ContractRow({ offer: o }: { offer: PublicContractOffer }) {
  const [open, setOpen] = useState(false);
  const { locale } = useTranslation("landing");
  const m = useTranslations("landing.publicEntry.pricing.compact");
  const money = (n: number) => formatPriceMinor(n, o.currency, locale);
  return (
    <tbody className={pricingBody}>
      <tr>
        <td className={`${pricingCell} text-left`}>
          <span className="flex flex-wrap items-baseline justify-start gap-x-1">
            <span className="text-sm text-slate-500">
              {o.installmentCount} ×
            </span>
            <span className={pricingAmount}>{money(o.monthlyAmountMinor)}</span>
          </span>
          <PricingConditions>
            <span className="block text-xs text-slate-500">
              {m("total", {
                price: money(o.monthlyAmountMinor * o.installmentCount),
              })}
            </span>
          </PricingConditions>
        </td>
        <th scope="row" className={pricingCell}>
          <span className={pricingName}>{o.name}</span>
          {o.description ? (
            <p className="mt-1 text-xs font-normal leading-5 text-slate-500">
              {o.description}
            </p>
          ) : null}
          {o.terms ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="block min-h-11 text-left text-xs font-medium text-[var(--landing-primary-600)] hover:underline"
            >
              {m("contractTerms")}
            </button>
          ) : null}
        </th>
        <td className={`${pricingCell} text-right`}>
          {m("duration", { count: o.durationMonths })}
        </td>
      </tr>
      <tr>
        <td colSpan={3} className="p-0">
          <Modal
            isOpen={open}
            onClose={() => setOpen(false)}
            size="large"
            title={o.name}
          >
            <div className="space-y-4 text-sm leading-6">
              <p>
                {o.installmentCount} × {money(o.monthlyAmountMinor)} ·{" "}
                {m("duration", { count: o.durationMonths })}
              </p>
              <p className="whitespace-pre-wrap">{o.terms}</p>
            </div>
          </Modal>
        </td>
      </tr>
    </tbody>
  );
}
